import type { TypePolicies } from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";

/**
 * Cache policy shared by the app client (src/main.tsx), Storybook stories, and
 * tests. Stories that render paginated surfaces must build their MockedProvider
 * cache from this — a policy-free cache silently exercises no merge behavior,
 * so a broken `fetchMore` would still look green.
 */
export const typePolicies: TypePolicies = {
	Query: {
		fields: {
			distinctNodeTypeNames: {
				merge(_existing = [], incoming) {
					return incoming;
				},
			},
		},
	},
	Edge: {
		fields: {
			// Keyset-paginated, newest-first. Field policies are stored per
			// normalized entity, so `Edge:<id>.events` is already isolated and
			// the default `keyArgs: false` is correct — `first`/`after` are the
			// only arguments. Refetching page 1 discards later pages by design.
			events: relayStylePagination(),
			// EdgePrivateContext is non-normalized (see below), and Apollo REPLACES
			// rather than deep-merges an incoming non-normalized object. Without
			// this, any query selecting a subset of its fields silently drops the
			// rest from the cache, leaving richer reads permanently incomplete.
			privateContext: { merge: true },
		},
	},
	// Neither type has an `id`, so Apollo already stores them inline. Declared
	// anyway because the failure mode if one ever gains an `id` is a privacy
	// leak, not a bug: EdgePrivateContext resolves the *calling user's* row, so
	// a normalized (shared) record could cross-read another user's private
	// summary within one session.
	EdgePrivateContext: { keyFields: false },
	EventParticipation: { keyFields: false },
};
